import { useState, useRef, ReactElement, ChangeEventHandler, KeyboardEventHandler, useEffect } from 'react'
import styled from 'styled-components'
import { min, max, isEmpty, isString, debounce, isArray, isBoolean, isObject } from 'lodash'

import EmptyState from 'components/v2/EmptyState'
import { SpinnerIcon } from 'components/Icons/SpinnerIcon'
import List from 'components/v2/List'
import { Input } from 'components/Input'
import Dropdown from 'components/v2/Dropdown'
import { EKeys } from 'utils/string'
import { IAutocompleteProps } from './types'
import { FormattedMessage } from 'react-intl'

const StyledSpinnerIcon = styled(SpinnerIcon)`
  fill: #5d5c70;
`

const StyledDropdownContent = styled(Dropdown.Content)<{ showError?: boolean }>`
  top: ${({ showError }): string => (showError ? 'calc(100% - 35px)' : '100%')};
`

type IInputObjectValue = Record<string, IInputValue>
type IInputValue = string | string[] | boolean | number | null | undefined
type IInputDependencies = string | boolean | number | null | undefined

const getInputsDependencies = (extraInputs: Record<string, IInputValue | IInputObjectValue>): IInputDependencies[] => {
  return Object.values(extraInputs).reduce((acc, inputValue) => {
    if (isArray(inputValue) || isObject(inputValue)) {
      return [...acc, JSON.stringify(inputValue)]
    }
    return [...acc, inputValue]
  }, [] as IInputDependencies[])
}

interface IUserDebouncedQueryOptions {
  query: TSFixMe // TODO: replace with the correct type https://kolors.atlassian.net/browse/NA-19131
  pattern: string
  delay?: number
  extraInputs?: Record<string, IInputValue | IInputObjectValue>
  skip?: boolean
}

interface IUserDebouncedQueryResult {
  isDebouncing: boolean
  shouldShowItems: boolean
}

export const useDebouncedQuery = ({
  query,
  pattern,
  extraInputs = {},
  delay = 300,
  skip
}: IUserDebouncedQueryOptions): IUserDebouncedQueryResult => {
  const [isDebouncing, setIsDebouncing] = useState(false)
  const debouncedQuery = useRef(
    debounce((pattern: string, extraInputs: Record<string, IInputValue | IInputObjectValue>) => {
      setIsDebouncing(false)
      query({
        fetchPolicy: 'network-only',
        variables: {
          input: {
            pattern,
            ...extraInputs
          }
        }
      })
    }, delay)
  ).current

  useEffect((): void => {
    const shouldTriggerQuery = !skip

    if (shouldTriggerQuery) {
      setIsDebouncing(true)
      debouncedQuery(pattern, extraInputs)
    }
  }, [pattern, ...getInputsDependencies(extraInputs), skip])

  return {
    isDebouncing,
    shouldShowItems: true
  }
}

export const Autocomplete = (props: IAutocompleteProps): ReactElement => {
  let dropdownContent
  const {
    onChange,
    label,
    items = [],
    loading,
    error,
    errorMessage,
    getItemLabel,
    emptyMessage,
    filteredValues = [],
    disableKeyboard,
    ...inputProps
  } = props
  const inputRef = useRef<HTMLInputElement>(null)
  const [value, setValue] = useState('')
  const [focusIndex, setFocusIndex] = useState(0)
  const [open, setOpen] = useState(false)

  const toggleDropdown = (): void => {
    setOpen(!open)
  }

  const handleClickAway = (): void => {
    setOpen(false)
  }

  const shownItems =
    items.filter((item) => {
      if (isString(item)) {
        return filteredValues.indexOf(item) === -1
      } else {
        const filteredIds = filteredValues.map(({ id }) => id)
        return filteredIds.indexOf(item.id) === -1
      }
    }) || []

  const handleChange: ChangeEventHandler<HTMLInputElement> = ({ target: { value } }): void => {
    if (disableKeyboard) {
      return
    }

    if (!open) {
      setOpen(true)
    }

    setValue(value)
    setFocusIndex(0)
    onChange(null, {
      inputValue: value
    })
  }

  const handleSelect = (item: TSFixMe): void => {
    onChange(item)

    setValue('')
    setFocusIndex(0)
    setOpen(false)
    inputRef.current?.focus()
  }

  const handleKeyDown: KeyboardEventHandler<HTMLInputElement> = (e): void => {
    switch (e.key) {
      case EKeys.ARROW_UP:
        e.preventDefault()
        if (open) {
          setFocusIndex(max([0, focusIndex - 1]) ?? 0)
        }
        break
      case EKeys.ARROW_DOWN:
        e.preventDefault()
        if (open) {
          if (!isEmpty(shownItems)) {
            setFocusIndex(min([shownItems.length, focusIndex + 1]) ?? 0)
          }
        } else {
          setOpen(true)
        }
        break
      case EKeys.ENTER:
        if (open) {
          e.preventDefault()
          if (!isEmpty(shownItems)) {
            handleSelect(shownItems[focusIndex])
          }
        }
        break
    }
  }

  if (isBoolean(props.shouldShowItems) && !props.shouldShowItems) {
    dropdownContent = (
      <EmptyState.Container>
        <FormattedMessage
          id="autocomplete.minCharacters.description"
          defaultMessage="Please enter at least 3 characters to start searching"
        />
      </EmptyState.Container>
    )
  } else if (loading) {
    dropdownContent = (
      <EmptyState.Container>
        <StyledSpinnerIcon />
      </EmptyState.Container>
    )
  } else if (error) {
    dropdownContent = <EmptyState.Container>{errorMessage}</EmptyState.Container>
  } else if (!isEmpty(items)) {
    dropdownContent = (
      <List.Container>
        {shownItems.map((item, index) => {
          return (
            <List.Item
              key={index}
              isFocused={focusIndex === index}
              onClick={(e: TSFixMe): void => {
                e.preventDefault()
                e.stopPropagation()

                handleSelect(item)
              }}
              action
            >
              {getItemLabel(item)}
            </List.Item>
          )
        })}
      </List.Container>
    )
  } else {
    dropdownContent = (
      <EmptyState.Container>
        <div>{emptyMessage}</div>
      </EmptyState.Container>
    )
  }

  return (
    <Dropdown.Wrapper onClickAway={handleClickAway} form>
      <label>
        <Input
          {...inputProps}
          ref={inputRef}
          label={label}
          autoComplete="off"
          value={inputProps.value || value}
          onChange={handleChange}
          onKeyDown={handleKeyDown}
          onClick={toggleDropdown}
          isOpen={open}
          isDropdown
        />

        <StyledDropdownContent
          open={open}
          noIndicator
          left
          form
          showError={Boolean(inputProps.touched && inputProps.errors)}
        >
          {dropdownContent}
        </StyledDropdownContent>
      </label>
    </Dropdown.Wrapper>
  )
}
